import { sigmaToPercentile } from '@/utils/personalityCalculations';

export interface ComparisonData {
  person1: {
    name: string;
    email: string;
    scores: {
      dominance: number;
      extraversion: number;
      patience: number;
      formality: number;
      objectivity: number;
    };
    selectedWords: string[];
  };
  person2: {
    name: string;
    email: string;
    scores: {
      dominance: number;
      extraversion: number;
      patience: number;
      formality: number;
      objectivity: number;
    };
    selectedWords: string[];
  };
  compatibility: {
    overall: number;
    byTrait: {
      dominance: number;
      extraversion: number;
      patience: number;
      formality: number;
      objectivity: number;
    };
  };
  insights: {
    strengths: string[];
    challenges: string[];
    recommendations: string[];
  };
}

// Convert sigma scores to percentage using canonical sigmaToPercentile (same as Dashboard)
export const sigmaToPercentage = (sigma: number): number => {
  return sigmaToPercentile(sigma);
};

// Extract SELF behavioral scores from either single or dual assessment data
const extractSelfScores = (rawScores: any) => {
  console.log('🔍 Extracting SELF scores from raw data:', rawScores);
  
  if (!rawScores || typeof rawScores !== 'object') {
    console.warn('❌ Invalid raw scores data');
    return null;
  }
  
  // Check if this is a dual assessment (has selfBehavior nested structure)
  if (rawScores.selfBehavior && rawScores.selfBehavior.scores) {
    console.log('✅ Dual assessment detected - extracting from selfBehavior.scores');
    return rawScores.selfBehavior.scores;
  }
  
  // Check if this is a single assessment (direct scores at top level)
  // Single assessments should have trait scores directly accessible
  const hasDirectTraits = ['dominance', 'extraversion', 'patience', 'formality', 'objectivity']
    .some(trait => rawScores[trait] !== undefined || rawScores[trait.charAt(0).toUpperCase()] !== undefined);
  
  if (hasDirectTraits) {
    console.log('✅ Single assessment detected - using top-level scores');
    return rawScores;
  }
  
  console.warn('⚠️ Unable to identify assessment type or extract scores');
  return null;
};

// Handle different score formats and convert to 0-100 scale
// Priority: stored percentiles (M3) > sigma conversion via canonical sigmaToPercentile
export const transformScores = (rawScores: any) => {
  console.log('🔍 Raw scores from database:', rawScores);
  
  // First extract the SELF scores from the data structure
  const selfScores = extractSelfScores(rawScores);
  
  if (!selfScores) {
    console.warn('❌ Could not extract SELF scores, using defaults');
    return {
      dominance: 50,
      extraversion: 50,
      patience: 50,
      formality: 50,
      objectivity: 50
    };
  }
  
  console.log('📊 Extracted SELF scores:', selfScores);

  // Helper: get a trait's percentile, preferring stored percentile over sigma conversion
  const getTraitPercentile = (traitName: string): number => {
    // 1) Check for stored percentile (M3 assessments store these directly)
    const percentileKey = `${traitName}Percentile`;
    const storedPercentile = selfScores[percentileKey];
    
    if (storedPercentile !== undefined && storedPercentile !== null) {
      const numVal = typeof storedPercentile === 'string' ? parseFloat(storedPercentile) : storedPercentile;
      if (!isNaN(numVal) && numVal >= 0 && numVal <= 100) {
        console.log(`✅ ${traitName}: using stored percentile ${numVal}`);
        return Math.round(numVal);
      }
    }
    
    // 2) Fall back to sigma/z-score conversion using canonical sigmaToPercentile
    const rawValue = selfScores[traitName];
    if (rawValue !== undefined && rawValue !== null) {
      const numVal = typeof rawValue === 'string' ? parseFloat(rawValue) : rawValue;
      if (!isNaN(numVal)) {
        // Sigma range check: values between -3 and 3 are likely sigma scores
        if (numVal >= -3 && numVal <= 3) {
          const converted = sigmaToPercentile(numVal);
          console.log(`🔄 ${traitName}: sigma ${numVal} → percentile ${converted}`);
          return converted;
        }
        // Already a percentage (0-100)
        if (numVal >= 0 && numVal <= 100) {
          console.log(`✅ ${traitName}: already percentage ${numVal}`);
          return Math.round(numVal);
        }
      }
    }
    
    console.warn(`⚠️ ${traitName}: no valid score found, using default 50`);
    return 50;
  };

  const transformedScores = {
    dominance: getTraitPercentile('dominance'),
    extraversion: getTraitPercentile('extraversion'), 
    patience: getTraitPercentile('patience'),
    formality: getTraitPercentile('formality'),
    objectivity: getTraitPercentile('objectivity')
  };

  console.log('✨ Final transformed SELF scores:', transformedScores);
  return transformedScores;
};

// Add validation function to check if AI analysis matches the data
const validateInsights = (person1: any, person2: any, insights: any): boolean => {
  console.log('🔍 Validating AI insights against actual scores...');
  
  const person1Scores = transformScores(person1.scores);
  const person2Scores = transformScores(person2.scores);
  
  // Check specific traits that commonly get misinterpreted
  const formalityDiff = person1Scores.formality - person2Scores.formality;
  const dominanceDiff = person1Scores.dominance - person2Scores.dominance;
  
  console.log(`📊 Order comparison: ${person1.full_name} (${person1Scores.formality}%) vs ${person2.full_name} (${person2Scores.formality}%)`);
  console.log(`📊 Drive comparison: ${person1.full_name} (${person1Scores.dominance}%) vs ${person2.full_name} (${person2Scores.dominance}%)`);
  
  // Log validation results
  if (Math.abs(formalityDiff) > 10) {
    const moreFormalPerson = formalityDiff > 0 ? person1.full_name : person2.full_name;
    console.log(`✅ Clear formality difference detected: ${moreFormalPerson} is more formal`);
  }
  
  if (Math.abs(dominanceDiff) > 10) {
    const moreDominantPerson = dominanceDiff > 0 ? person1.full_name : person2.full_name;
    console.log(`✅ Clear dominance difference detected: ${moreDominantPerson} is more dominant`);
  }
  
  return true; // For now, always return true but log the validation
};

export const calculateCompatibility = (scores1: any, scores2: any) => {
  console.log('🤝 Calculating compatibility between:', scores1, scores2);
  
  const normalizedScores1 = transformScores(scores1);
  const normalizedScores2 = transformScores(scores2);
  
  const traits = ['dominance', 'extraversion', 'patience', 'formality', 'objectivity'] as const;
  
  const byTrait = {
    dominance: 0,
    extraversion: 0,
    patience: 0,
    formality: 0,
    objectivity: 0
  };

  traits.forEach(trait => {
    const score1 = normalizedScores1[trait];
    const score2 = normalizedScores2[trait];
    const diff = Math.abs(score1 - score2);
    const compatibility = Math.max(0, 100 - diff);
    byTrait[trait] = compatibility;
    console.log(`📈 ${trait}: ${score1}% vs ${score2}% = ${compatibility}% compatibility`);
  });

  const overall = Object.values(byTrait).reduce((sum, val) => sum + val, 0) / traits.length;
  console.log('🎯 Overall compatibility:', overall);

  return { overall, byTrait };
};

export const generateInsights = async (person1: any, person2: any, compatibility: any): Promise<{ strengths: string[]; challenges: string[]; recommendations: string[] }> => {
  try {
    console.log('🤖 Starting AI insights generation for:', person1.full_name, 'vs', person2.full_name);
    
    // Transform scores and log them for validation
    const person1Scores = transformScores(person1.scores);
    const person2Scores = transformScores(person2.scores);
    
    console.log('📊 Sending scores to AI:', {
      person1: { name: person1.full_name, scores: person1Scores },
      person2: { name: person2.full_name, scores: person2Scores },
      compatibility
    });
    
    // Validate insights before returning
    const isValid = validateInsights(person1, person2, null);
    console.log('✅ Validation result:', isValid);
    
    // Import supabase client dynamically to avoid SSR issues
    const { supabase } = await import('@/integrations/supabase/client');
    
    console.log('📡 Calling generate-insights Edge Function...');
    
    const { data, error } = await supabase.functions.invoke('generate-insights', {
      body: {
        person1: {
          name: person1.full_name,
          scores: person1Scores
        },
        person2: {
          name: person2.full_name,
          scores: person2Scores
        },
        compatibility
      }
    });

    console.log('📥 Edge Function response:', { data, error });

    if (error) {
      console.error('❌ Edge Function error:', error);
      throw new Error(`Edge Function failed: ${error.message}`);
    }

    if (data?.insights) {
      console.log('✅ AI insights generated successfully:', data.insights);
      return data.insights;
    }

    if (data?.fallback) {
      console.warn('⚠️ AI generation failed, using fallback insights');
      throw new Error('AI service returned fallback response');
    }

    throw new Error('No insights returned from AI service');

  } catch (error) {
    console.warn('⚠️ AI insights generation failed, using enhanced fallback:', error);
    
    // Enhanced fallback with more natural, conversational insights
    return generateEnhancedFallbackInsights(person1, person2, compatibility);
  }
};

// Enhanced fallback insights with more conversational, AI-like analysis
const generateEnhancedFallbackInsights = (person1: any, person2: any, compatibility: any): { strengths: string[]; challenges: string[]; recommendations: string[] } => {
  const person1Scores = transformScores(person1.scores);
  const person2Scores = transformScores(person2.scores);
  
  console.log('🔄 Generating enhanced fallback insights with accurate trait analysis...');
  console.log(`📊 ${person1.full_name} scores:`, person1Scores);
  console.log(`📊 ${person2.full_name} scores:`, person2Scores);

  const insights = [];
  
  // Start with overall compatibility assessment
  if (compatibility.overall > 80) {
    insights.push(`${person1.full_name} and ${person2.full_name} demonstrate exceptional workplace compatibility, with naturally aligned working styles that create a foundation for seamless collaboration.`);
  } else if (compatibility.overall > 60) {
    insights.push(`These two professionals bring complementary strengths to their collaboration, with enough common ground to work effectively while offering diverse perspectives that can enhance outcomes.`);
  } else {
    insights.push(`While ${person1.full_name} and ${person2.full_name} have different working styles, this diversity can be leveraged strategically with the right communication frameworks and mutual understanding.`);
  }

  // Analyze specific trait combinations with CORRECT interpretation
  const dominanceDiff = Math.abs(person1Scores.dominance - person2Scores.dominance);
  const extraDiff = Math.abs(person1Scores.extraversion - person2Scores.extraversion);
  const patienceDiff = Math.abs(person1Scores.patience - person2Scores.patience);
  const formalityDiff = Math.abs(person1Scores.formality - person2Scores.formality);
  
  // CORRECT formality analysis
  if (formalityDiff > 20) {
    const moreFormalPerson = person1Scores.formality > person2Scores.formality ? person1.full_name : person2.full_name;
    const lessFormalPerson = person1Scores.formality < person2Scores.formality ? person1.full_name : person2.full_name;
    console.log(`📝 Order insight: ${moreFormalPerson} (${person1Scores.formality > person2Scores.formality ? person1Scores.formality : person2Scores.formality}%) is more formal than ${lessFormalPerson} (${person1Scores.formality < person2Scores.formality ? person1Scores.formality : person2Scores.formality}%)`);
    insights.push(`${moreFormalPerson}'s structured, detail-oriented approach complements ${lessFormalPerson}'s more flexible, adaptable style, creating a balance between thorough planning and agile execution.`);
  }
  
  // CORRECT dominance analysis
  if (dominanceDiff > 20) {
    const moreDominantPerson = person1Scores.dominance > person2Scores.dominance ? person1.full_name : person2.full_name;
    const lessAssertivePerson = person1Scores.dominance < person2Scores.dominance ? person1.full_name : person2.full_name;
    console.log(`👑 Drive insight: ${moreDominantPerson} (${person1Scores.dominance > person2Scores.dominance ? person1Scores.dominance : person2Scores.dominance}%) is more dominant than ${lessAssertivePerson} (${person1Scores.dominance < person2Scores.dominance ? person1Scores.dominance : person2Scores.dominance}%)`);
    insights.push(`${moreDominantPerson}'s natural leadership drive pairs well with ${lessAssertivePerson}'s collaborative approach, creating a dynamic where decisive action is balanced with inclusive decision-making.`);
  }
  
  if (dominanceDiff < 20 && extraDiff < 20) {
    insights.push(`Both individuals share similar leadership and communication approaches, which naturally reduces friction in decision-making processes and team interactions.`);
  }
  
  if (patienceDiff > 30) {
    const fastPerson = person1Scores.patience < person2Scores.patience ? person1.full_name : person2.full_name;
    const steadyPerson = person1Scores.patience > person2Scores.patience ? person1.full_name : person2.full_name;
    insights.push(`The dynamic between ${fastPerson}'s urgency-driven approach and ${steadyPerson}'s methodical pace can create powerful momentum when properly balanced, combining rapid execution with thorough consideration.`);
  }

  // Always include a forward-looking insight
  insights.push(`With intentional collaboration and mutual respect for their different strengths, this partnership has strong potential for achieving exceptional results together.`);

  // Format into the expected structure (but display as narrative)
  return {
    strengths: insights.slice(0, Math.ceil(insights.length / 2)),
    challenges: [],
    recommendations: insights.slice(Math.ceil(insights.length / 2))
  };
};

export const getTraitDescription = (trait: string, score: number): string => {
  const descriptions = {
    dominance: {
      high: "Direct, results-focused, takes charge",
      medium: "Balanced approach to leadership",
      low: "Cooperative, seeks harmony, supportive"
    },
    extraversion: {
      high: "Outgoing, talkative, energetic",
      medium: "Adaptable social style",
      low: "Reserved, thoughtful, prefers smaller groups"
    },
    patience: {
      high: "Steady, reliable, prefers consistency",
      medium: "Balanced pace and approach",
      low: "Fast-paced, urgent, seeks quick results"
    },
    formality: {
      high: "Structured, detail-oriented, follows procedures",
      medium: "Flexible with standards",
      low: "Casual, adaptable, big-picture focused"
    },
    objectivity: {
      high: "Analytical, data-driven, logical",
      medium: "Balanced decision-making",
      low: "People-focused, empathetic, relationship-oriented"
    }
  };

  const level = score > 70 ? 'high' : score < 30 ? 'low' : 'medium';
  return descriptions[trait as keyof typeof descriptions][level];
};
